import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import './PaymentPage.css';

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planName = 'Bilinmeyen Plan', amount = '' } = location.state || {};

  const [formAmount, setFormAmount] = useState(amount);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [userAddress, setUserAddress] = useState('');
  const [userIp, setUserIp] = useState('');
  const [merchantOid, setMerchantOid] = useState('');
  const [paymentToken, setPaymentToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Firebase Functions'ı başlat
  const functions = getFunctions();
  const createPaymentToken = httpsCallable(functions, 'createPaymentToken');

  // Kullanıcı ve IP bilgisini al
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setError('Kullanıcı oturum açmamış. Lütfen giriş yapın.');
        navigate('/login');
      } else {
        setUserEmail(user.email || '');
        setUserName(user.displayName || '');
        // merchantOid'i alfanumerik olacak şekilde oluştur: order + userId + timestamp + randomString
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substr(2, 9);
        const uniqueOid = `order${user.uid}${timestamp}${randomString}`;
        console.log('Generated merchantOid:', uniqueOid); // Oluşturulan merchantOid'i logla
        setMerchantOid(uniqueOid);
      }
    });

    const fetchUserIp = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setUserIp(data.ip);
      } catch (err) {
        setError('IP adresi alınamadı. Lütfen internet bağlantınızı kontrol edin.');
      }
    };
    fetchUserIp();

    return () => unsubscribe();
  }, [navigate]);

  const validateForm = () => {
    if (!formAmount || isNaN(formAmount) || formAmount <= 0) {
      setError('Geçersiz ödeme tutarı.');
      return false;
    }
    if (!userEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      setError('Geçersiz e-posta adresi.');
      return false;
    }
    if (!userName || userName.length > 60) {
      setError('Ad soyad 60 karakterden kısa olmalı.');
      return false;
    }
    if (!userPhone || !/^\d{10,20}$/.test(userPhone)) {
      setError('Geçersiz telefon numarası (10-20 rakam olmalı).');
      return false;
    }
    if (!userAddress || userAddress.length > 400) {
      setError('Adres 400 karakterden kısa olmalı.');
      return false;
    }
    if (!userIp || !/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(userIp)) {
      setError('Geçersiz IP adresi.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setError('Kullanıcı oturum açmamış. Lütfen tekrar giriş yapın.');
      setLoading(false);
      return;
    }

    const paymentData = {
      amount: parseFloat(formAmount),
      userEmail,
      userName,
      userPhone,
      userAddress,
      userIp,
      merchantOid,
    };

    console.log('Sending paymentData:', paymentData);

    try {
      // Firebase Functions üzerinden createPaymentToken fonksiyonunu çağır
      const result = await createPaymentToken(paymentData);
      console.log('Payment token result:', result.data);

      if (result.data.status === 'success') {
        setPaymentToken(result.data.token);
      } else {
        throw new Error(result.data.error || 'Ödeme işlemi başarısız');
      }
    } catch (err) {
      console.error('Payment Error:', err);
      setError(`Ödeme işlemi başarısız: ${err.message || 'Bilinmeyen hata'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="payment-page">
      <h2>Ödeme Sayfası</h2>
      <p>Seçilen Plan: {planName}</p>
      {!paymentToken ? (
        <form onSubmit={handleSubmit} className="payment-form">
          <div className="form-group">
            <label htmlFor="amount">Ödeme Tutarı (TL):</label>
            <input
              type="number"
              id="amount"
              value={formAmount}
              readOnly
              required
              min="1"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="userName">Ad Soyad:</label>
            <input
              type="text"
              id="userName"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              required
              maxLength="60"
            />
          </div>
          <div className="form-group">
            <label htmlFor="userEmail">E-posta:</label>
            <input
              type="email"
              id="userEmail"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="userPhone">Telefon:</label>
            <input
              type="tel"
              id="userPhone"
              value={userPhone}
              onChange={(e) => setUserPhone(e.target.value)}
              required
              placeholder="ör: 5551234567"
              pattern="\d{10,20}"
            />
          </div>
          <div className="form-group">
            <label htmlFor="userAddress">Adres:</label>
            <textarea
              id="userAddress"
              value={userAddress}
              onChange={(e) => setUserAddress(e.target.value)}
              required
              maxLength="400"
            />
          </div>
          <div className="form-group">
            <label htmlFor="merchantOid">Sipariş ID’si:</label>
            <input type="text" id="merchantOid" value={merchantOid} readOnly />
          </div>
          <button type="submit" disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Ödemeyi Başlat'}
          </button>
          {error && <p className="error">{error}</p>}
        </form>
      ) : (
        <div>
          <iframe
            src={`https://www.paytr.com/odeme/guvenli/${paymentToken}`}
            id="paytriframe"
            frameBorder="0"
            scrolling="yes"
            style={{ width: '100%', height: '600px' }}
            title="PayTR Ödeme Ekranı"
          />
        </div>
      )}
    </div>
  );
};

export default PaymentPage;